import { useState, useEffect } from 'react'
import { Loader2, DollarSign } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import { useToast } from '@hooks/use-toast'
import { billingApi } from '@services/billing.service'
import { Invoice } from '@/types'
import { formatCurrency } from '@/lib/utils'

const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'credit_card', label: 'Tarjeta de Crédito' },
  { value: 'debit_card', label: 'Tarjeta de Débito' },
  { value: 'check', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Transferencia Bancaria' },
  { value: 'insurance', label: 'Seguro' },
  { value: 'online_payment', label: 'Pago en Línea' },
]

interface Props {
  invoice: Invoice | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function RecordPaymentDialog({ invoice, open, onOpenChange, onSuccess }: Props) {
  const { toast } = useToast()
  const [amount, setAmount] = useState('0')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [paidBy, setPaidBy] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Default the amount to the outstanding balance and the payer to the patient
  // each time the dialog is opened for a (possibly different) invoice.
  useEffect(() => {
    if (open && invoice) {
      setAmount(String(Number(invoice.balance_due) || 0))
      setPaidBy(invoice.patient_full_name || '')
      setPaymentMethod('cash')
      setNotes('')
    }
  }, [open, invoice])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!invoice) return

    const numericAmount = parseFloat(amount)
    if (!numericAmount || numericAmount <= 0) {
      toast({ title: 'Monto inválido', description: 'Ingresa un monto mayor a cero.', variant: 'destructive' })
      return
    }
    if (!paidBy.trim()) {
      toast({ title: 'Campo requerido', description: 'Indica quién realiza el pago.', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      await billingApi.processPayment(invoice.id, {
        invoice_id: invoice.id,
        amount: numericAmount,
        payment_method: paymentMethod,
        paid_by: paidBy.trim(),
        notes: notes.trim() || undefined,
      })
      toast({ title: 'Pago registrado', description: `Se registró un pago de ${formatCurrency(numericAmount)}.` })
      onSuccess?.()
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'No se pudo registrar el pago',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar Pago</DialogTitle>
          <DialogDescription>
            {invoice
              ? `Factura ${invoice.invoice_number} • Saldo: ${formatCurrency(Number(invoice.balance_due))}`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="payment_amount">Monto *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ml-on-surface-variant/60" />
              <Input
                id="payment_amount"
                type="number"
                min={0}
                step="0.01"
                className="pl-9"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_method">Método de Pago *</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger id="payment_method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paid_by">Pagado Por *</Label>
            <Input
              id="paid_by"
              value={paidBy}
              onChange={(e) => setPaidBy(e.target.value)}
              placeholder="Nombre de quien paga"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_notes">Notas</Label>
            <Textarea
              id="payment_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas del pago (opcional)"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-ml-primary text-white hover:bg-ml-primary-container"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                'Registrar Pago'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
